import { z } from 'zod';

const envSchema = z.object({
  EXPO_PUBLIC_FIREBASE_API_KEY: z
    .string()
    .min(1, 'Firebase API key is required.'),
  EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN: z
    .string()
    .min(1, 'Firebase auth domain is required.'),
  EXPO_PUBLIC_FIREBASE_PROJECT_ID: z
    .string()
    .min(1, 'Firebase project ID is required.'),
  EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET: z
    .string()
    .min(1, 'Firebase storage bucket is required.'),
  EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: z
    .string()
    .min(1, 'Firebase messaging sender ID is required.'),
  EXPO_PUBLIC_FIREBASE_APP_ID: z
    .string()
    .min(1, 'Firebase app ID is required.'),
  EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID: z.string().optional().default(''),
  EXPO_PUBLIC_FIREBASE_VAPID_KEY: z.string().optional().default(''),
  EXPO_PUBLIC_GIPHY_API_KEY: z.string().optional().default(''),
  EXPO_PUBLIC_USE_FIREBASE_EMULATORS: z
    .enum(['true', 'false'])
    .optional()
    .default('false'),
  EXPO_PUBLIC_FACEBOOK_APP_ID: z.string().optional().default('954504137471896'),
  EXPO_PUBLIC_GOOGLE_AUTH_ENABLED: z
    .enum(['true', 'false'])
    .optional()
    .default('true'),
  EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID: z.string().optional().default(''),
  EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID: z.string().optional().default(''),
  EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID: z.string().optional().default(''),
  EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().optional().default(''),
});

const parsedEnv = envSchema.safeParse({
  EXPO_PUBLIC_FIREBASE_API_KEY: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN:
    process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  EXPO_PUBLIC_FIREBASE_PROJECT_ID: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET:
    process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
  EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID:
    process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  EXPO_PUBLIC_FIREBASE_APP_ID: process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
  EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID:
    process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
  EXPO_PUBLIC_FIREBASE_VAPID_KEY: process.env.EXPO_PUBLIC_FIREBASE_VAPID_KEY,
  EXPO_PUBLIC_GIPHY_API_KEY: process.env.EXPO_PUBLIC_GIPHY_API_KEY,
  EXPO_PUBLIC_USE_FIREBASE_EMULATORS:
    process.env.EXPO_PUBLIC_USE_FIREBASE_EMULATORS,
  EXPO_PUBLIC_FACEBOOK_APP_ID: process.env.EXPO_PUBLIC_FACEBOOK_APP_ID,
  EXPO_PUBLIC_GOOGLE_AUTH_ENABLED: process.env.EXPO_PUBLIC_GOOGLE_AUTH_ENABLED,
  EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID:
    process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID,
  EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID:
    process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID,
  EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID:
    process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID,
  EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY:
    process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY,
});

if (!parsedEnv.success) {
  const message = parsedEnv.error.issues
    .map((issue) => issue.message)
    .join('\n');
  throw new Error(`Invalid environment configuration:\n${message}`);
}

export const env = parsedEnv.data;

export const firebaseConfig = {
  apiKey: env.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain: env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.EXPO_PUBLIC_FIREBASE_APP_ID,
  ...(env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID
    ? { measurementId: env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID }
    : {}),
};

export const analyticsConfig = {
  measurementId: env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

export const messagingConfig = {
  vapidKey: env.EXPO_PUBLIC_FIREBASE_VAPID_KEY,
};

export const giphyConfig = {
  apiKey: env.EXPO_PUBLIC_GIPHY_API_KEY,
  rating: 'pg',
};

export const firebaseEmulatorConfig = {
  enabled: env.EXPO_PUBLIC_USE_FIREBASE_EMULATORS === 'true',
  host: '127.0.0.1',
  ports: {
    auth: 9099,
    firestore: 8080,
    functions: 5001,
    storage: 9199,
  },
};

export const facebookConfig = {
  appId: env.EXPO_PUBLIC_FACEBOOK_APP_ID,
};

export const googleConfig = {
  androidClientId: env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID,
  enabled: env.EXPO_PUBLIC_GOOGLE_AUTH_ENABLED === 'true',
  iosClientId: env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID,
  webClientId: env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID,
};

export const stripeConfig = {
  publishableKey: env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY,
};
